<template>
    <div>
        <Modal ref="modal" title="Raise Purchase Invoice" size="is-xlarge" paddingless @closed="close()">
            <div class="p-3">
                <p>
                    Search for an approved purchase order in order to raise a purchase invoice.
                    <br />
                    You can also raise a single invoice across multiple purchase orders providing that the suppliers are
                    the same.
                </p>
                <div v-for="(result, index) in selectedPurchaseOrders" :key="result.id" class="po-row">
                    <div>{{ result.reference }}</div>
                    <div>
                        <div>{{ result.title }}</div>
                        <small>
                            {{ result.supplier ? result.supplier.name : 'No Supplier' }}
                        </small>
                    </div>
                    <FormField class="tw-pr-4 tw-ml-auto" label="Currency">
                        {{ result.target_currency }}
                    </FormField>
                    <FormField label="Total">
                        {{ localise(result.total, 2, true) }}
                    </FormField>
                    <FormField label="Invoiced">
                        {{ localise(result.total_invoiced, 2, true) }}
                    </FormField>
                    <FormField label="Paid">
                        {{ localise(result.total_paid, 2, true) }}
                    </FormField>
                    <Button
                        title="Remove from selected"
                        class="text-danger"
                        type="is-link"
                        @click="removeSelection(index)"
                    >
                        <i class="mdi mdi-close"></i>
                    </Button>
                </div>
                <Textbox
                    v-model="searchQuery"
                    class="tw-mt-3"
                    placeholder="Search for an approved purchase order by reference, title, or supplier"
                />
                <Button
                    v-for="result in searchResults"
                    :key="result.id"
                    class="po-row tw-w-full"
                    @click="selectPurchaseOrder(result)"
                >
                    <span>{{ result.reference }}</span>
                    <div>
                        <div>{{ result.title }}</div>
                        <small>
                            {{ result.supplier ? result.supplier.name : 'No Supplier' }}
                        </small>
                    </div>
                    <FormField class="tw-pr-4 tw-ml-auto" label="Currency">
                        {{ result.target_currency }}
                    </FormField>
                    <FormField label="Total">
                        {{ localise(result.total, 2, true) }}
                    </FormField>
                    <FormField label="Invoiced">
                        {{ localise(result.total_invoiced, 2, true) }}
                    </FormField>
                    <FormField label="Paid">
                        {{ localise(result.total_paid, 2, true) }}
                    </FormField>
                </Button>
                <LoadingSpinner v-if="isLoading" />
                <p v-else-if="!searchResults?.length" class="empty-label mt-2 tw-mb-0">
                    There are no approved purchase orders that match your query
                </p>
            </div>
            <template #footer>
                <Button @click="close()">Cancel</Button>
                <Button
                    type="is-success"
                    class="tw-ml-auto"
                    :disabled="!selectedPurchaseOrders.length"
                    @click="openRaiseInvoiceModal()"
                >
                    Continue
                </Button>
            </template>
        </Modal>
    </div>
</template>
<script setup>
import debounce from 'lodash/debounce';
import Modal from '@/js/components/Modal.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import { toast } from '@/js/utils.js';
import LoadingSpinner from '@/js/components/widgets/LoadingSpinner.vue';
import { ref, watch, onMounted, useTemplateRef } from 'vue';

const isLoading = ref(false);
const searchQuery = ref(null);
const searchResults = ref(null);
const selectedPurchaseOrders = ref([]);
const modal = useTemplateRef('modal');

const openRaiseInvoiceModal = () => {
    Eventbus.$emit('purchaseInvoice:create', selectedPurchaseOrders.value);
    close();
};
const close = () => {
    isLoading.value = false;
    searchQuery.value = null;
    searchResults.value = null;
    selectedPurchaseOrders.value = [];
    modal.value?.close();
};
const search = () => {
    isLoading.value = true;
    axios
        .get(
            route('api.account.event.purchaseorders', {
                perPage: 5,
                query: searchQuery.value,
                supplier: selectedPurchaseOrders.value[0]?.supplier_id || null,
                exclude: selectedPurchaseOrders.value.map((p) => p.id),
            })
        )
        .then(({ data }) => {
            searchResults.value = data.data;
        })
        .finally(() => (isLoading.value = false));
};
const debouncedSearch = debounce(function () {
    search();
}, 500);
const selectPurchaseOrder = (purchaseOrder) => {
    if (
        selectedPurchaseOrders.value.length &&
        selectedPurchaseOrders.value[0].target_currency !== purchaseOrder.target_currency
    ) {
        toast(
            'Error',
            'You can only raise a single invoice across multiple purchase orders if the suppliers are the same and the currencies match.',
            'error'
        );
        return;
    }
    selectedPurchaseOrders.value.push(purchaseOrder);
    if (searchResults.value.length === 1) {
        searchQuery.value = null;
        searchResults.value = null;
    } else {
        search();
    }
};
const removeSelection = (index) => {
    selectedPurchaseOrders.value.splice(index, 1);
    if (searchQuery.value) {
        search();
    }
};

watch(searchQuery, () => {
    if (!searchQuery.value) {
        searchResults.value = null;
        return;
    }
    debouncedSearch();
});

onMounted(() => {
    Eventbus.$on('create:supplierInvoice', () => modal.value?.open());
});
</script>
<style scoped>
.po-row {
    @apply tw-flex tw-gap-3 tw-items-center tw-text-left tw-border tw-rounded-md tw-px-2 tw-my-1;
    & small {
        @apply tw-text-slate-400;
    }
    & dl {
        @apply tw-text-right;
    }
}
</style>
